import React from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import Seo from "../components/site/Seo";
import { Container, Eyebrow } from "../components/site/Ui";
import { COMPANY, DOCS, DOC_INDEX, LAST_REVIEWED, TODO, isFilled } from "../data/legal";

const Placeholder = ({ children }) => (
  <span className="rounded-md bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-900">{children}</span>
);

/** Renders text and turns every [TO BE COMPLETED] marker into a visible, editable placeholder. */
const Text = ({ value }) =>
  String(value)
    .split(/(\[TO BE COMPLETED[^\]]*\])/g)
    .map((part, i) =>
      part.startsWith("[TO BE COMPLETED") ? (
        <Placeholder key={`${i}-${part}`}>{part}</Placeholder>
      ) : (
        <React.Fragment key={`${i}-${part.slice(0, 12)}`}>{part}</React.Fragment>
      )
    );

const CompanyTable = () => {
  const rows = [
    ["Trading name", COMPANY.tradingName],
    ["Registered business name", COMPANY.registeredName],
    ["Registered address", COMPANY.registeredAddress],
    ["KVK number", COMPANY.kvk],
    ["VAT / BTW number", COMPANY.vat],
    ["Email", COMPANY.email],
    ["Website", COMPANY.website],
    ["Telephone", COMPANY.telephone],
  ];
  return (
    <dl className="mt-4 divide-y divide-slate-100" data-testid="legal-company-details">
      {rows.map(([label, value]) => (
        <div key={label} className="flex flex-wrap gap-2 py-2.5 text-sm">
          <dt className="w-56 shrink-0 font-semibold text-slate-800">{label}</dt>
          <dd className="min-w-0 flex-1 text-slate-600">
            {isFilled(value) ? value : <Placeholder>{TODO}</Placeholder>}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const Section = ({ section }) => (
  <article className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow sm:p-8">
    <h2 className="font-display text-xl font-semibold text-slate-900">{section.title}</h2>
    {section.body && (
      <p className="mt-3 text-sm leading-relaxed text-slate-600">
        <Text value={section.body} />
      </p>
    )}
    {section.list && (
      <ul className="mt-4 space-y-2.5">
        {section.list.map((item) => (
          <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-slate-600">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F95738]" aria-hidden="true" />
            <span>
              <Text value={item} />
            </span>
          </li>
        ))}
      </ul>
    )}
    {section.company && <CompanyTable />}
    {section.note === "confirm" && (
      <p className="mt-4 flex gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          <strong>Confirm before publishing. </strong>
          {section.noteText}
        </span>
      </p>
    )}
  </article>
);

const Index = () => (
  <>
    <Seo title="Legal — PostMint" description="All PostMint legal and privacy documents in one place." path="/legal" />
    <section className="mesh-warm" data-testid="legal-index-hero">
      <Container className="py-16">
        <Eyebrow>Legal</Eyebrow>
        <h1 className="mt-5 text-4xl font-extrabold text-slate-900 sm:text-5xl">Legal &amp; privacy</h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
          Everything about how PostMint works, what we do with your data and how your subscription is billed.
        </p>
      </Container>
    </section>
    <section className="py-16">
      <Container>
        <div className="grid gap-4 md:grid-cols-2" data-testid="legal-index-list">
          {DOC_INDEX.map(([slug, description]) => (
            <Link
              key={slug}
              to={`/legal/${slug}`}
              data-testid={`legal-index-${slug}`}
              className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow transition-transform hover:-translate-y-0.5"
            >
              <p className="font-display text-lg font-semibold text-slate-900">{DOCS[slug].title}</p>
              <p className="mt-2 text-sm text-slate-600">{description}</p>
            </Link>
          ))}
        </div>
      </Container>
    </section>
  </>
);

export default function Legal() {
  const { slug } = useParams();
  if (!slug) return <Index />;

  const page = DOCS[slug];
  if (!page) {
    return (
      <Container className="py-24" data-testid="legal-not-found">
        <h1 className="text-3xl font-extrabold text-slate-900">Document not found</h1>
        <Link to="/legal" className="mt-4 inline-flex font-semibold text-[#F95738] hover:underline">
          See all legal documents
        </Link>
      </Container>
    );
  }

  return (
    <>
      <Seo title={`${page.title} — PostMint`} description={page.intro} path={`/legal/${slug}`} />

      <section className="mesh-warm" data-testid="legal-hero">
        <Container className="py-16">
          <Eyebrow>Legal</Eyebrow>
          <h1 className="mt-5 text-4xl font-extrabold text-slate-900 sm:text-5xl">{page.title}</h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600">{page.intro}</p>
          <p className="mt-5 text-xs text-slate-500">
            Last reviewed: {isFilled(LAST_REVIEWED) ? LAST_REVIEWED : <Placeholder>{TODO}</Placeholder>}
          </p>
        </Container>
      </section>

      <section className="py-16">
        <Container className="grid gap-10 lg:grid-cols-[0.28fr_0.72fr]">
          <nav aria-label="On this page" className="lg:sticky lg:top-24 lg:self-start" data-testid="legal-toc">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">On this page</p>
            <ul className="mt-3 space-y-2">
              {page.sections.map((s) => (
                <li key={s.title} className="text-sm text-slate-500">
                  {s.title}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-slate-400">Other documents</p>
            <ul className="mt-3 space-y-2">
              {DOC_INDEX.filter(([s]) => s !== slug).map(([s]) => (
                <li key={s}>
                  <Link
                    to={`/legal/${s}`}
                    data-testid={`legal-nav-${s}`}
                    className="text-sm text-slate-500 hover:text-[#F95738]"
                  >
                    {DOCS[s].title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-5" data-testid="legal-sections">
            {page.sections.map((s) => (
              <Section key={s.title} section={s} />
            ))}
            <p className="text-sm text-slate-500" data-testid="legal-footer-note">
              {COMPANY.brandName} · {COMPANY.website} · {COMPANY.email} · Registered in {COMPANY.country}
              {isFilled(COMPANY.kvk) ? ` · KVK ${COMPANY.kvk}` : ""}
              {isFilled(COMPANY.vat) ? ` · VAT ${COMPANY.vat}` : ""}
            </p>
          </div>
        </Container>
      </section>
    </>
  );
}
